/**
 * DiagDistributions: Diagnostics drawer -- ranked tenor and moneyness distributions, moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Display only.
 */
import React from "react";
import { Card, CardContent } from "@components/ui/card";
import { badgeStyle, mutedText, progressFillStyle, progressTrackStyle, secondaryText, subCardStyle } from "@components/screener/screenerUi";
import { pctFmt, titleizeBucket } from "@lib/screenerTruth";
import { structuralAnchor, toneFromTenorBucket } from "@lib/screenerViewHelpers";
import { type Purchase, type Tone } from "@lib/screenerTypes";

export type DiagDistributionsProps = {
  allCandidateRows: Purchase[];
  maxTenorPct: number;
  moneynessBehavior: { baseBand: number; bands: { label: string; upper: number | null; count: number; pct: number; }[]; expansionActive: boolean; widestUsed: number; };
  tenorMixRows: { bucket: string; label: string; count: number; pct: number; tone: Tone; }[];
};

export default function DiagDistributions({
  allCandidateRows,
  maxTenorPct,
  moneynessBehavior,
  tenorMixRows,
}: DiagDistributionsProps) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1fr]">
      <Card style={subCardStyle}>
        <CardContent className="p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">Ranked Tenor Distribution</div>
              <div className="mt-1 text-xs" style={mutedText}>
                Contract-count distribution by tenor bucket across the ranked universe. No notional caps are applied in market-rank mode.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span style={badgeStyle("neutral")}>Largest bucket: {pctFmt(maxTenorPct, 1)}</span>
            </div>
          </div>

          <div className="space-y-3">
            {tenorMixRows.length ? (
              tenorMixRows.map((row) => (
                <div key={row.bucket}>
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{row.label}</span>
                      {row.bucket === structuralAnchor && (
                        <span style={badgeStyle("info")}>Core</span>
                      )}
                      <span style={badgeStyle(row.tone)}>{pctFmt(row.pct, 1)}</span>
                    </div>
                    <div style={secondaryText}>{`${row.count} contracts`}</div>
                  </div>
                  <div style={progressTrackStyle()}>
                    <div
                      style={progressFillStyle(row.pct * 100, toneFromTenorBucket(row.bucket))}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm" style={mutedText}>
                No ranked tenor distribution available yet.
              </div>
            )}
          </div>

          {/* CORE 24–30m COVERAGE: the anchor bucket's ranked count, rendered outside the tenorMixRows list on
              purpose -- "core got nothing this run" is exactly the case worth seeing. */}
          <div className="mt-3 text-xs" style={mutedText}>
            Core {titleizeBucket(structuralAnchor)}:{" "}
            {`${allCandidateRows.filter((p) => p.tenorBucket === structuralAnchor).length} ranked contracts`}
          </div>
        </CardContent>
      </Card>

      <Card style={subCardStyle}>
        <CardContent className="p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold">Ranked Moneyness Distribution</div>
              <div className="mt-1 text-xs" style={mutedText}>
                Contract-count distribution by moneyness band across the ranked universe.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span style={badgeStyle(moneynessBehavior.expansionActive ? "warning" : "success")}>
                {moneynessBehavior.expansionActive ? "Expansion Active" : "Base Band Only"}
              </span>
              <span style={badgeStyle("neutral")}>Base {pctFmt(moneynessBehavior.baseBand, 0)}</span>
            </div>
          </div>

          <div className="space-y-3">
            {moneynessBehavior.bands.map((row) => (
              <div key={row.label}>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{row.label}</span>
                    <span style={badgeStyle("neutral")}>{row.count} {"contracts"}</span>
                  </div>
                  <div style={secondaryText}>
                    {`${row.count} contracts`} · {pctFmt(row.pct, 1)}
                  </div>
                </div>
                <div style={progressTrackStyle()}>
                  <div
                    style={progressFillStyle(
                      row.pct * 100,
                      row.upper == null ? "danger" : row.upper <= 0.05 ? "success" : row.upper <= 0.06 ? "warning" : "info"
                    )}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
